/* Google Workspace sign-in gate (Phase 0 auth, Task 5).
 *
 * Backend surface (service/auth.py + app/routers/auth.py — already merged,
 * see the branch's task-5 brief): GET /auth/config -> {clientId, devMode};
 * POST /auth/google {credential} -> sets the fb_session cookie (httponly,
 * same-origin) + {user}; GET /auth/me -> {user} or 401 "sign-in required";
 * POST /auth/logout clears the cookie. In FB_AUTH_DEV=1 dev mode /auth/me
 * never 401s — it always resolves to the fake "Dev AM" user (service/
 * auth.py's require_user), so showGate() below is simply never reached in
 * that mode.
 *
 * AUTH.init() gates main.ts's boot() (see main.ts's doc comment on the
 * gate) — nothing else fetches client/task/run data until a session is
 * confirmed one way or another. api/client.ts's request() also calls
 * showGate() reactively on any LATER 401 (a session that expires mid-use),
 * via a dynamic import to avoid a circular static import between the two
 * modules.
 */
import { html, render } from 'lit';
import { ApiError, request } from '../api/client.ts';
import type { PersonaRow } from './personas.ts';

export interface AuthUser {
  id: number;
  email: string;
  name: string;
  picture_url: string | null;
  role: string;
  // Persona-foundation (Task 3): /auth/me already returns these (backend
  // task, merged) — optional here because the field predates this branch's
  // seed data and every test-constructed AuthUser literal in the existing
  // suite omits them; heldCapacities()/resolveLanding() (lib/personas.ts)
  // treat missing personas as "holds nothing" / missing primary as "no
  // default capacity", which is exactly the pre-persona behavior.
  personas?: PersonaRow[];
  primary_persona?: string | null;
}

interface AuthConfig {
  clientId: string;
  devMode: boolean;
}

// Minimal ambient surface for the Google Identity Services script
// (index.html's <head> loads https://accounts.google.com/gsi/client, which
// attaches `google` to `window`) — no @types package is installed for it,
// and the hub needs only these two calls.
declare global {
  interface Window {
    google?: {
      accounts: {
        id: {
          initialize(config: { client_id: string; callback: (resp: { credential: string }) => void }): void;
          renderButton(parent: HTMLElement, options: Record<string, unknown>): void;
        };
      };
    };
  }
}

class Auth {
  user: AuthUser | null = null;
  // Re-entrancy guard: several in-flight requests can 401 at once when a
  // session expires mid-use; without this each one would re-render the gate
  // (and re-init GIS) on top of the last. Reset by dismissGate() / a reload.
  private _gateVisible = false;

  /** Resolves the signed-in user, showing the sign-in gate (and blocking on
   * it) when the session is missing/expired. Returns `false` only when the
   * gate was shown — callers use that to skip their own pre-auth fetches;
   * any OTHER failure (service unreachable) returns `true` so the existing
   * offline fallbacks (WS.init()'s own try/catch, the demo-payload load in
   * main.ts's boot()) still run exactly as they did before this task. */
  async init(): Promise<boolean> {
    try {
      const r = await request<{ user: AuthUser }>('/auth/me');
      this.user = r.user;
      return true;
    } catch (e) {
      if (e instanceof ApiError && e.status === 401) {
        await this.showGate();
        return false;
      }
      return true;
    }
  }

  /** Injects/repaints the full-page #authGate overlay (styles/app.css) and
   * renders the official GIS button into it. Resolves once the overlay is
   * showing — NOT once the user has signed in.
   *
   * `reauth` distinguishes the two callers (design review #1):
   *  - COLD BOOT (init(), reauth omitted/false): no session at page load,
   *    nothing rendered underneath yet. Success reloads to run boot() fresh.
   *  - MID-SESSION EXPIRY (api/client.ts's reactive 401, reauth:true): the
   *    AM is mid-edit — open note textareas, the custom-task dialog, drawer
   *    drafts are all live in the DOM under the overlay. Success re-confirms
   *    the session and DISMISSES the overlay WITHOUT a reload, so that work
   *    survives; the copy says "session expired", not cold-boot framing. */
  async showGate(opts: { reauth?: boolean } = {}): Promise<void> {
    const reauth = !!opts.reauth;
    if (this._gateVisible) return; // see _gateVisible's note (concurrent 401s)
    this._gateVisible = true;

    let el = document.getElementById('authGate');
    if (!el) {
      el = document.createElement('div');
      el.id = 'authGate';
      document.body.appendChild(el);
    }
    const host = el;
    const heading = reauth ? 'Session expired' : 'Sign in';

    let cfg: AuthConfig;
    try {
      cfg = await request<AuthConfig>('/auth/config');
    } catch (e) {
      render(html`<div class="authcard">
        <h2>${heading}</h2>
        <div class="mh">Could not reach the service (${String((e as Error).message || e)}). Start the backend and reload.</div>
      </div>`, host);
      return;
    }

    if (!cfg.clientId) {
      render(html`<div class="authcard">
        <h2>${heading}</h2>
        <div class="mh">Google sign-in is not configured on the server (GOOGLE_CLIENT_ID missing) — ask the project owner to set it up.</div>
      </div>`, host);
      return;
    }

    render(html`<div class="authcard">
      <img class="authmark" src="/design-system/assets/fiveblocks-mark.svg" alt="Five Blocks">
      <h2>${heading}</h2>
      <div class="mh">${reauth
        ? 'Your session expired. Sign in again to pick up where you left off — your unsaved work on this page is preserved.'
        : 'Sign in with your Five Blocks Google Workspace account to open the workspace.'}</div>
      <div id="gsiBtn"></div>
      <div class="autherr" id="authErr"></div>
      <div class="authhint">Don't see a Google pop-up? Allow pop-ups and third-party cookies for this site, then try again — or open the hub in a regular (non-incognito) window.</div>
    </div>`, host);

    if (!window.google) {
      const errEl = document.getElementById('authErr');
      if (errEl) errEl.textContent = 'Google sign-in script failed to load — check your connection and reload.';
      return;
    }

    const callback = async (resp: { credential: string }) => {
      try {
        await request('/auth/google', { method: 'POST', body: JSON.stringify({ credential: resp.credential }) });
        if (reauth) {
          // Non-destructive re-auth (design review #1): re-confirm the
          // session and drop the overlay, leaving the page (and every open
          // draft under it) exactly as it was — no reload.
          try {
            const me = await request<{ user: AuthUser }>('/auth/me');
            this.user = me.user;
          } catch (e) { /* session couldn't be re-read; still dismiss so the page isn't stuck behind the overlay */ }
          this.dismissGate();
        } else {
          location.reload();
        }
      } catch (e) {
        const detail = e instanceof ApiError ? e.detail : null;
        const msg = (detail && typeof detail === 'object' && (detail as { error?: unknown }).error) || detail || (e as Error).message || String(e);
        const errEl = document.getElementById('authErr');
        if (errEl) errEl.textContent = String(msg);
      }
    };

    // FedCM-degraded / cookie-partitioned environments can make initialize()
    // or renderButton() throw synchronously (design review #4). Rather than a
    // silent dead end, surface an actionable message. (A blocked *popup* on
    // the button's own click isn't reported to JS by GIS at all — that's what
    // the always-visible .authhint above covers.)
    try {
      window.google.accounts.id.initialize({ client_id: cfg.clientId, callback });
      const btnHost = host.querySelector('#gsiBtn') as HTMLElement;
      window.google.accounts.id.renderButton(btnHost, { theme: 'outline', size: 'large' });
    } catch (e) {
      const errEl = document.getElementById('authErr');
      if (errEl) errEl.textContent = 'Google sign-in could not start in this browser (' + String((e as Error | null)?.message || e)
        + '). Allow pop-ups and third-party cookies for this site, or try a different browser, then reload.';
    }
  }

  /** Removes the #authGate overlay and clears the re-entrancy guard — the
   * non-reloading "unblock" signal for a mid-session re-auth. */
  dismissGate(): void {
    this._gateVisible = false;
    const el = document.getElementById('authGate');
    if (el) el.remove();
  }

  async signOut(): Promise<void> {
    try { await request('/auth/logout', { method: 'POST' }); } catch (e) { /* clearing client-side state below is enough either way */ }
    location.reload();
  }
}

export const AUTH = new Auth();
